/**
 * Generation guard for a directory load that cannot be cancelled.
 *
 * `begin()` starts a generation and returns its token; only the latest generation is
 * current. A load checks `isCurrent(token)` before applying its results so a slower,
 * older load cannot land under a newer directory. `invalidate()` supersedes the current
 * generation without starting another (component unmount).
 *
 * The once-per-load root-item-count claim lives here too: it is a per-generation fact,
 * and keying it on anything else (the site URL, previously) let a stale trigger arm it
 * for a site before that site's libraries had arrived.
 */

export interface IDirectoryLoadTracker {
    begin(): number;
    invalidate(): void;
    isCurrent(token: number): boolean;
    claimRootItemCounts(): boolean;
}

const NO_GENERATION = 0;

export const createDirectoryLoadTracker = (): IDirectoryLoadTracker => {
    let nextToken = NO_GENERATION;
    let current = NO_GENERATION;
    let rootItemCountsClaimedFor = NO_GENERATION;

    return {
        begin: () => {
            current = ++nextToken;
            return current;
        },
        invalidate: () => {
            current = NO_GENERATION;
        },
        isCurrent: token => token !== NO_GENERATION && token === current,
        claimRootItemCounts: () => {
            if (current === NO_GENERATION || rootItemCountsClaimedFor === current) {
                return false;
            }
            rootItemCountsClaimedFor = current;
            return true;
        },
    };
};
